import * as Sentry from '@sentry/browser';
import queryString from 'query-string';
import { useCookies } from 'react-cookie';

import { SaleCaseFileTypeValue, SaleCasesType } from '@type-def_iqfu/SaleCasesType';

import { UserType } from '@constants_common';
import { DealType, MediateType, PurchaseType, SaleCasesSortKey, SaleCasesStatusType } from '@constants_iqfu';

const backendBasePath = process.env.NEXT_PUBLIC_DEV_BACKEND_URL;

export type SaleCase = {
  id: number;
  status: 'draft' | 'unapproved' | 'rejected' | 'approved'; // 承認ステータス。
  is_public: boolean;
  company_name: string; // 不動産会社名。未承認かつインフォメーションマップがない実績にはマップなしの文言をつけて返す。
  contract_date: string; // format: date, example: '2021-10-20', 売買契約年月日。
  deal_type: 'mediation' | 'purchase'; // 取引方法。
  purchase_type: 'other_company' | 'in_house'; // 買取タイプ。
  mediate_type: Extract<keyof typeof MediateType, 'buying_and_selling' | 'selling'>;
  price: number; // 売買価格。単位は万円。
  property_type: 'mansion' | 'land' | 'used_house' | 'new_house'; // 物件種別。
  full_address: string; // 都道府県、市区町村も結合した住所を返す。マンションはマンション名も末尾に追加する。
  destroyable: boolean; // 削除が可能か不可能か。
  created_at: string;
  seller_company_review: SaleCasesType['seller_company_review'];
};

export type CompanyDetailSaleCasesMapBounds = {
  min_lat: number | null;
  max_lat: number | null;
  min_long: number | null;
  max_long: number | null;
};

export type CompanyDetailSaleCaseMapPin = {
  id: number;
  company_id: number;
  display_name: string;
  mansion_id: number;
  mansion_name: string;
  city: {
    code: string;
    name: string;
  };
  town: {
    code: string;
    name: string;
  };
  property_type: 'mansion' | 'land' | 'used_house' | 'new_house';
  //中心座標
  lat: number;
  long: number;
};

export type CompanyDetailSaleCases = {
  id: number;
  lat: number;
  long: number;
  company_type: 'general_agency' | 'purchase_agency';
  sale_case_map_pins: CompanyDetailSaleCaseMapPin[];
  sale_case_map_bounds?: CompanyDetailSaleCasesMapBounds;
};

export const useSaleCase = () => {
  const [cookies] = useCookies(['expiry', 'access-token', 'client', 'uid', 'user-type']);

  const getSaleCasesList = async (companyId: number): Promise<CompanyDetailSaleCases | undefined> => {
    try {
      const response = await fetch(`${backendBasePath}/companies/${companyId}`, { method: 'GET', mode: 'cors' });

      if (response.status === 404) return undefined;
      const result = await response.json();
      return {
        id: result.id,
        lat: result.lat,
        long: result.long,
        company_type: result.company_type,
        sale_case_map_pins: result.sale_case_map_pins,
        sale_case_map_bounds: result.sale_case_map_bounds,
      };
    } catch (error) {
      Sentry.captureException(error);
      console.error('通信に失敗しました', error);
      return undefined;
    }
  };

  const getSaleCases = async (
    userType: UserType,
    queryParams: {
      offset: number;
      limit: number;
      company_id?: string;
      address?: string;
      mansion_name?: string;
      property_type?: string;
      status?: '' | keyof typeof SaleCasesStatusType;
      has_information_map?: '' | 'true' | 'false';
      created_date_from?: string;
      created_date_to?: string;
      contract_date_from?: string;
      contract_date_to?: string;
      deal_type?: '' | keyof typeof DealType;
      purchase_type?: '' | keyof typeof PurchaseType;
      sort_key?: '' | keyof typeof SaleCasesSortKey;
      sort_order?: '' | 'asc' | 'desc';
      is_public?: boolean | null;
    },
  ): Promise<{
    offset: number; // 取得データのoffset
    limit: number; // 取得データの件数
    total_count: number; // 取得条件のデータ総数
    sale_cases: SaleCase[];
  }> => {
    if (queryParams.offset < 0) queryParams.offset = 0;
    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/${userType}/sale_cases?${query}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getSaleReasons = async () => {
    let url = `${backendBasePath}/sale_reasons`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getSaleCaseDetail = async (sale_case_id: string, userType: UserType) => {
    const url = `${backendBasePath}/${userType}/sale_cases/${sale_case_id}`;
    let status;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 404) throw new Error();
        status = 'success';
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        status = 'error';
      });
    return { status, result };
  };

  const createSaleCase = async (formData: FormData) => {
    const url = `${backendBasePath}/company/sale_cases`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: formData,
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        let plainObject: { [key: string]: FormDataEntryValue } = {};
        formData.forEach((value: FormDataEntryValue, key: string) => {
          plainObject[key] = value;
        });
        Sentry.withScope((scope) => {
          scope.setExtra('payload', plainObject);
          Sentry.captureException(error);
        });
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteSaleCase = async (sale_case_id: string, userType: UserType) => {
    const url = `${backendBasePath}/${userType}/sale_cases/${sale_case_id}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateSaleCase = async (sale_case_id: string, formData: FormData) => {
    const url = `${backendBasePath}/company/sale_cases/${sale_case_id}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: formData,
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        let plainObject: { [key: string]: FormDataEntryValue } = {};
        formData.forEach((value: FormDataEntryValue, key: string) => {
          plainObject[key] = value;
        });
        Sentry.withScope((scope) => {
          scope.setExtra('payload', plainObject);
          Sentry.captureException(error);
        });
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const approveSaleCase = async (
    sale_case_id: string,
    submitData: {
      is_public: boolean;
      status: 'draft' | 'unapproved' | 'rejected' | 'approved';
      rejected_reason: string;
      file_type?: SaleCaseFileTypeValue;
      deal_type?: string;
      purchase_type?: string;
      note?: string;
      occupied_area?: number;
      occupied_area_type?: string;
      floor_plan?: string;
      land_area?: number;
      land_area_detail?: string;
      land_category?: string;
      land_right?: string;
      building_area?: number;
      built_date?: string;
      building_structure?: string;
      home_inspection?: boolean;
      address_type?: 'lot_number' | 'residential_address';
    },
  ) => {
    const url = `${backendBasePath}/admin/sale_cases/${sale_case_id}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const uploadCsvFile = async (formData: FormData) => {
    const url = `${backendBasePath}/admin/sale_cases/import`;
    let status;
    const results = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: formData,
    })
      .then((response) => {
        if (response.status === 200) {
          status = 'success';
          return response.json();
        } else {
          status = response.status;
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return { status, results };
  };

  type ReformItemOption = {
    id: number;
    name: string;
    description: string;
    property_type: 'mansion' | 'used_house' | 'new_house';
  };
  const getReformItems = async (
    userType: UserType,
    property_type: 'mansion' | 'new_house' | 'used_house',
  ): Promise<{ reform_items: ReformItemOption[] }> => {
    const url = `${backendBasePath}/${userType}/reform_items?property_type=${property_type}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const previewSecureFile = async (userType: UserType, secure_token: string) => {
    const url = `${backendBasePath}/${userType}/sale_case_secure_files/${secure_token}/download`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.blob();
      })
      .then((blob) => {
        return URL.createObjectURL(blob);
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error(error);
      });
    return result;
  };

  return {
    getSaleCasesList,
    getSaleCases,
    getSaleCaseDetail,
    getSaleReasons,
    createSaleCase,
    updateSaleCase,
    deleteSaleCase,
    approveSaleCase,
    uploadCsvFile,
    getReformItems,
    previewSecureFile,
  };
};
